import { useState, useRef, useEffect } from "react";
import GroupIcon from "@mui/icons-material/Group";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PhoneIcon from "@mui/icons-material/Phone";
import VideocamIcon from "@mui/icons-material/Videocam";
import { useCall } from "../../../../../context/temp";

export default function ChatHeader({
  currentChat,
  otherUser,
  userId,
  isOtherUserOnline,
  connectionStatus,
  currentUnreadCount,
  onHeaderClick,
  messages = [],
  onSearchResultClick,
}) {
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);

  const BASE_DOWNLOAD_URL = process.env.REACT_APP_API_BASE_URL_DOWNLOAD;
  const searchInputRef = useRef(null);

  // Call functionality
  const { initiateCall, activeCall } = useCall();

  const handleVoiceCall = (e) => {
    e.stopPropagation();
    if (activeCall || !otherUser?.isActive) return;
    initiateCall(currentChat._id, 'voice', otherUser);
  };

  const handleVideoCall = (e) => {
    e.stopPropagation();
    if (activeCall || !otherUser?.isActive) return;
    initiateCall(currentChat._id, 'video', otherUser);
  };

  // Check if call buttons should be disabled
  const isCallDisabled = !!activeCall || !otherUser?.isActive;
  const callDisabledReason = !otherUser?.isActive 
    ? "User is not available for calls" 
    : "Call in progress";

  // Profile / Avatar
  const getProfilePicUrl = () => {
    if (otherUser?.profilePic) {
      const baseUrl = otherUser.profilePic.startsWith("http")
        ? otherUser.profilePic
        : `${BASE_DOWNLOAD_URL}${otherUser.profilePic}`;
      return `${baseUrl}?v=${otherUser.updatedAt || Date.now()}`;
    }
    return null;
  };

  const getAvatar = () => {
    if (currentChat?.isGroupChat) {
      if (currentChat.groupPic) {
        return (
          <img
            src={`${BASE_DOWNLOAD_URL}${currentChat.groupPic}`}
            alt={currentChat.chatName || "Group"}
            className="w-full h-full object-cover"
          />
        );
      }
      return <GroupIcon className="text-gray-600" />;
    } else {
      const profilePicUrl = getProfilePicUrl();
      return profilePicUrl ? (
        <img
          src={profilePicUrl}
          alt={otherUser.username || "user"}
          className="w-full h-full object-cover"
        />
      ) : (
        `${otherUser.firstName?.[0] || ""}${otherUser.lastName?.[0] || ""}`.toUpperCase() ||
        "U"
      );
    }
  };

  const displayName = currentChat?.isGroupChat
    ? currentChat.chatName || "Group Chat"
    : `${otherUser?.firstName || ""} ${otherUser?.lastName || ""}`.trim() ||
      "Unknown User";

  // Search
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setCurrentResultIndex(-1);
      removeHighlights();
      return;
    }

    const query = searchQuery.toLowerCase();
    const newResults = messages
      .map((msg, index) => ({ ...msg, originalIndex: index }))
      .filter((msg) => msg.content?.toLowerCase().includes(query))
      .reverse();

    setSearchResults((prev) => {
      const same =
        prev.length === newResults.length &&
        prev.every((r, i) => r._id === newResults[i]._id);

      if (!same) {
        setCurrentResultIndex(newResults.length > 0 ? 0 : -1);
      }

      return newResults;
    });
  }, [searchQuery, messages]);

  useEffect(() => {
    if (
      currentResultIndex >= 0 &&
      searchResults.length > 0 &&
      currentResultIndex < searchResults.length
    ) {
      const result = searchResults[currentResultIndex];
      scrollToAndHighlight(result._id, searchQuery.toLowerCase());
    }
  }, [currentResultIndex, searchResults]);

  const scrollToAndHighlight = (messageId, query) => {
    if (!messageId) return;

    if (onSearchResultClick) {
      onSearchResultClick(messageId);
    }

    const el = document.querySelector(`[data-message-id="${messageId}"]`);
    if (el) {
      document.querySelectorAll(".search-highlight").forEach((e) =>
        e.classList.remove("search-highlight")
      );
      el.classList.add("search-highlight");
    }
  };

  const removeHighlights = () => {
    document.querySelectorAll(".search-highlight").forEach((e) =>
      e.classList.remove("search-highlight")
    );
  };

  const handleSearchOpen = (e) => {
    e.stopPropagation();
    setShowSearch(true);
    setSearchQuery("");
    setSearchResults([]);
    setCurrentResultIndex(0);
    removeHighlights();
  };

  const handleSearchClose = () => {
    setShowSearch(false);
    setSearchQuery("");
    setSearchResults([]);
    setCurrentResultIndex(0);
    removeHighlights();
  };

  const handleNextResult = () => {
    if (searchResults.length === 0) return;
    const newIndex =
      currentResultIndex > 0 ? currentResultIndex - 1 : searchResults.length - 1;
    setCurrentResultIndex(newIndex);

    const result = searchResults[newIndex];
    if (result) {
      scrollToAndHighlight(result._id, searchQuery.toLowerCase());
    }
  };

  const handlePrevResult = () => {
    if (searchResults.length === 0) return;
    const newIndex =
      currentResultIndex < searchResults.length - 1 ? currentResultIndex + 1 : 0;
    setCurrentResultIndex(newIndex);

    const result = searchResults[newIndex];
    if (result) {
      scrollToAndHighlight(result._id, searchQuery.toLowerCase());
    }
  };

  return (
    <div className="border-b bg-white shadow-sm relative">
      {!showSearch ? (
        <div
          onClick={onHeaderClick}
          className="flex cursor-pointer items-center justify-between p-3"
        >
          <div className="flex items-center gap-3">
            <div className="relative w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-sm font-bold text-gray-700 overflow-hidden">
              {getAvatar()}
              {!currentChat?.isGroupChat && (
                <div
                  className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
                    isOtherUserOnline ? "bg-green-500" : "bg-gray-400"
                  }`}
                ></div>
              )}
            </div>

            <div>
              <div className="font-semibold text-sm flex items-center gap-2">
                {displayName}
                {!currentChat?.isGroupChat && !otherUser?.isActive && (
                  <span className="text-[8px] px-2 bg-red-100 text-red-600 rounded-full">
                    Inactive
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500">
                {currentChat?.isGroupChat ? (
                  `${currentChat.users.length} members`
                ) : isOtherUserOnline ? (
                  <span className="text-green-600">Online</span>
                ) : (
                  `Last seen ${new Date(
                    otherUser.updatedAt || otherUser.lastSeen
                  ).toLocaleString()}`
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Call Buttons - Only for 1-on-1 chats */}
            {!currentChat?.isGroupChat && otherUser && (
              <>
                <button
                  onClick={handleVoiceCall}
                  disabled={isCallDisabled}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={isCallDisabled ? callDisabledReason : "Voice call"}
                >
                  <PhoneIcon className="text-gray-600" fontSize="small" />
                </button>

                <button
                  onClick={handleVideoCall}
                  disabled={isCallDisabled}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={isCallDisabled ? callDisabledReason : "Video call"}
                >
                  <VideocamIcon className="text-gray-600" fontSize="small" />
                </button>
              </>
            )}

            <button
              onClick={handleSearchOpen}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              title="Search messages"
            >
              <SearchIcon className="text-gray-600" fontSize="small" />
            </button>

            <div
              className={`w-2 h-2 rounded-full ${
                connectionStatus === "connected"
                  ? "bg-green-500"
                  : connectionStatus === "error"
                  ? "bg-red-500"
                  : "bg-yellow-500"
              }`}
              title={`Connection: ${connectionStatus}`}
            ></div>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 p-3 bg-gray-50">
          <button
            onClick={handleSearchClose}
            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
          >
            <ArrowBackIcon className="text-gray-600" fontSize="small" />
          </button>

          <div className="flex-1 relative">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search messages..."
              className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-full focus:outline-none focus:border-blue-500 bg-white text-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
              >
                <CloseIcon fontSize="small" className="text-gray-500" />
              </button>
            )}
          </div>

          {searchResults.length > 0 && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-600 whitespace-nowrap">
                {currentResultIndex + 1} of {searchResults.length}
              </span>
              <button
                onClick={handlePrevResult}
                className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                title="Previous result"
              >
                <KeyboardArrowUpIcon fontSize="small" className="text-gray-600" />
              </button>
              <button
                onClick={handleNextResult}
                className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                title="Next result"
              >
                <KeyboardArrowDownIcon fontSize="small" className="text-gray-600" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}